/**
 * [BLURY] | 🥭 drawShapeLayer
 * Paints the sharp shape layer — base gradient, radial shapes, horizon band —
 * onto an over-scanned buffer, so the blur pass has real pixels to pull from at
 * the edges instead of fading into transparency.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { mix, rgba } from './color';
import type { Composition, Size } from './types';

/** How much padding the blur pass needs around the visible canvas. */
export function overscanFor(blur: number): number {
  return Math.ceil(blur * 2.2) + 4;
}

export function drawShapeLayer(
  ctx: CanvasRenderingContext2D,
  composition: Composition,
  { width, height }: Size,
  pad: number,
): void {
  const cw = width + pad * 2;
  const ch = height + pad * 2;
  ctx.clearRect(0, 0, cw, ch);

  // Base vertical field. Gradient stops clamp beyond the visible area, so the
  // over-scan padding stays solid colour rather than transparent.
  const base = ctx.createLinearGradient(0, pad, 0, pad + height);
  base.addColorStop(0.0, mix(composition.deep, composition.cyan, 0.34));
  base.addColorStop(0.22, mix(composition.deep, composition.cyan, 0.1));
  base.addColorStop(0.45, composition.deep);
  base.addColorStop(0.7, mix(composition.deep, composition.cyan, 0.42));
  base.addColorStop(1.0, composition.cyan);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, cw, ch);

  // Shapes. Radius is keyed to sqrt(w * h) rather than width alone, so a shape
  // keeps roughly the same visual weight when the ratio changes.
  const globalAlpha = composition.opacity / 100;
  const scale = composition.scale / 100;
  const radiusBasis = Math.sqrt(width * height);

  for (const shape of composition.shapes) {
    const x = pad + shape.x * width;
    const y = pad + shape.y * height;
    const r = Math.max(2, shape.r * radiusBasis * scale);
    const alpha = shape.a * globalAlpha;

    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0.0, rgba(shape.c, alpha));
    g.addColorStop(0.45, rgba(shape.c, alpha * 0.72));
    g.addColorStop(0.78, rgba(shape.c, alpha * 0.22));
    g.addColorStop(1.0, rgba(shape.c, 0));

    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Pale horizon band, drawn last so it sits over everything.
  const hy = pad + (composition.horizon / 100) * height;
  const soft = Math.max(8, (composition.hsoft / 100) * height);
  const band = ctx.createLinearGradient(0, hy, 0, Math.min(hy + soft, pad + height + pad));
  band.addColorStop(0.0, rgba(composition.pale, 0));
  band.addColorStop(0.45, rgba(composition.pale, 0.45));
  band.addColorStop(0.8, rgba(composition.pale, 0.92));
  band.addColorStop(1.0, rgba(composition.pale, 1));
  ctx.fillStyle = band;
  ctx.fillRect(0, hy, cw, ch - hy);
}
